import {
  IsDateString,
  IsNotEmpty,
  IsOptional,
  IsNumber,
  ValidateIf,
  Validator,
} from 'class-validator';
import { Client, WhiteBloodCellDifferentialUnitType, BloodworkNotes } from '.';
import { IsSameOrBeforeNow } from './validators/IsSameOrBeforeNow';
import { MathUtils } from '../utils/MathUtils';
import { MenstrualPhase } from './MenstrualPhase';

const isNumericMsg: string = 'must have a numeric value.';

export class Bloodwork {
  static RED_BLOOD_CELLS: string = 'redBloodCells';
  static WHITE_BLOOD_CELLS = 'whiteBloodCells';
  static ABSOLUTE = 'Absolute';
  static PERCENT = 'Percent';

  // TODO: This cannot be empty
  practitionerId?: string; // the logged in user subject (partiton key)

  // TODO: This cannot be empty
  createDate?: string; // ISO-8601 date (sort key)
  lastModifiedDate: string; // ISO 8601 Date

  id?: string; // uuid4 uniquely identifes bloodwork

  @IsNotEmpty({ message: 'must be selected.' })
  wbcDifferentialUnits: WhiteBloodCellDifferentialUnitType;

  @IsNotEmpty({ message: 'must be selected.' })
  clientId?: string; // maps to client.id

  client: Client;

  @IsDateString({ message: 'must be a valid Date.' })
  @IsSameOrBeforeNow({ message: 'must not be in the future.' })
  collectionDate: string; // ISO-8601 date

  // Note: This field is optional unless we have a WBC Differencial
  // Regardless, if it's entered, it must be a number
  @IsNotEmpty({ message: 'is required when any WBC Differential is entered.' })
  @IsNumber({}, { message: isNumericMsg })
  @ValidateIf(
    (b: Bloodwork) => b.isWhiteBloodCellsDefined() || b.hasWBCDifferencial(),
  )
  whiteBloodCells?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  redBloodCells?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  hemoglobin?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  hematocrit?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  meanCorpuscularVolume?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  meanCorpuscularHGB?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  mchConcentration?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  rbcDistributionWidth?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  plateletCount?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  meanPlateletVolume?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  neutrophils?: number; // Stored as a percentage (44.9% is stored as 0.449)

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  lymphocytes?: number; // Stored as a percentage (44.9% is stored as 0.449)

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  monocytes?: number; // Stored as a percentage (44.9% is stored as 0.449)

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  eosinophils?: number; // Stored as a percentage (44.9% is stored as 0.449)

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  basophils?: number; // Stored as a percentage (44.9% is stored as 0.449)

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  nlr?: number;

  // Start of CMP related attributes
  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  glucose?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  height?: number; // cm — overrides client profile height for this test

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  weight?: number; // lbs — overrides client profile weight for this test

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  bun?: number; // Blood Urea Nitrogen

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  creatinine?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  eGFR?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  eGFRforAA?: number; // African American

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  bunCreatinineRatio?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  sodium?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  potassium?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  chloride?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  carbonDioxide?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  // Not stored in DB
  anionGap?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  // Not stored in DB
  anionGapWithoutK?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  calcium?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  protein?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  albumin?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  globulin?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  albuminGlobulinRatio?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  bilirubin?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  alkalinePhosphatase?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  ast?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  alt?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  sodiumPotassiumRatio?: number;
  // End of CMP related attributes

  // Start of Lipid(Fat) related attributes
  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  cholesterol?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  hdl?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  triglycerides?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  ldl?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  cholesterolHDLRatio?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  triglyceridesHDLRatio?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  apolipoproteinA1?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  apolipoproteinB?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  apoBapoA1Ratio?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  lipoproteinA?: number;
  // End of Lipid(Fat) related attributes

  // Start of Iron related attributes
  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  iron?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  tibc?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  saturation?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  ferritin?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  transferrin?: number;
  // End of Iron related attributes

  // Start of Additional Markers
  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  amylase?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  bileAcids?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  bilirubinDirect?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  bilirubinIndirect?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  ceruloplasmin?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  copperRBCμgmL?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  copperRBCμgdL?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  copperSerumμgdL?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  copperSerummcgmL?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  cpeptide?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  dDimer?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  fibrinogen?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  folateRBC?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  folateSerum?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  ggt?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  hemoglobinA1c?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  homocysteine?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  hsCRP?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  esr?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  insulin?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  lactateDehydrogenase?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  magnesiumRBC?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  magnesium?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  phosphorus?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  psaTotal?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  psaFree?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  psaFreePercent?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  reticulocyteCount?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  uricAcid?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  vitaminA?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  vitaminB6?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  vitaminB12?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  vitaminD?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  vitaminD125?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  zincRBCμgdL?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  zincRBCmgL?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  zincSerumμgdL?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  zincSerummcgmL?: number;
  // End of Additional Markers

  // Start of Thyroid Markers
  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  tsh?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  t4Total?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  t4Free?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  t3Total?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  t3Free?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  reverseT3?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  tpoAb?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  tgAb?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  tbg?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  t3Uptake?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  freeT4Index?: number;
  // End of Thyroid Markers

  // Start of Hormones Markers
  @IsOptional()
  menstrualPhase: MenstrualPhase; // Required for PRE menopausal females when estradiolE2 is populated

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  antiMullerianHormone?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  cortisolAM?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  cortisolPM?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  dheaS?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  dihydrotestosterone?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  estradiolE2?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  estriolE3?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  estrogensTotal?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  estroneE1?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  fsh?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  hCG?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  igf1?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  lh?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  pregnenolone?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  progesterone?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  hydroxyprogesterone17?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  prolactin?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  prolactinLactating?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  shbg?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  testosteroneBioavailable?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  testosteroneBioavailablePercent?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  testosteroneFreeCalc?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  testosteroneFreeDirect?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  testosteroneTotal?: number;

  @IsOptional()
  @IsNumber({}, { message: isNumericMsg })
  testosteroneTotalLCMS?: number;
  // End of Hormones Markers

  @IsOptional()
  bloodworkNotes?: BloodworkNotes;

  constructor(bloodwork?: Bloodwork | any) {
    if (!bloodwork) {
      return;
    }
    Object.keys(bloodwork).forEach(key => {
      this[key] = bloodwork[key];
    });
    if (bloodwork.client) {
      this.client = new Client(bloodwork.client);
    } else {
      this.client = new Client();
    }

    if (bloodwork.bloodworkNotes) {
      this.bloodworkNotes = new BloodworkNotes(bloodwork.bloodworkNotes);
    } else {
      this.bloodworkNotes = new BloodworkNotes();
    }
  }

  isNew?(): boolean {
    return this.id == null;
  }

  isTooOld(): boolean {
    return this.client.age > 200;
  }

  convertWBCDifferentialsIfNecessary?(
    wbcDifferentialUnits: WhiteBloodCellDifferentialUnitType,
  ) {
    if (!this.hasWBCDifferencial()) {
      return;
    }

    if (wbcDifferentialUnits === WhiteBloodCellDifferentialUnitType.PERCENT) {
      this.neutrophils = this.convertToDecimal(this.neutrophils);
      this.lymphocytes = this.convertToDecimal(this.lymphocytes);
      this.monocytes = this.convertToDecimal(this.monocytes);
      this.eosinophils = this.convertToDecimal(this.eosinophils);
      this.basophils = this.convertToDecimal(this.basophils);
    } else {
      this.neutrophils = this.convertToDecimalFromAbsolute(
        this.neutrophils,
        wbcDifferentialUnits,
      );
      this.lymphocytes = this.convertToDecimalFromAbsolute(
        this.lymphocytes,
        wbcDifferentialUnits,
      );
      this.monocytes = this.convertToDecimalFromAbsolute(
        this.monocytes,
        wbcDifferentialUnits,
      );
      this.eosinophils = this.convertToDecimalFromAbsolute(
        this.eosinophils,
        wbcDifferentialUnits,
      );
      this.basophils = this.convertToDecimalFromAbsolute(
        this.basophils,
        wbcDifferentialUnits,
      );
    }
  }

  convertWBCDifferentials?(
    wbcDifferentialUnits: WhiteBloodCellDifferentialUnitType,
  ) {
    if (!this.hasWBCDifferencial()) {
      return;
    }
    if (wbcDifferentialUnits === WhiteBloodCellDifferentialUnitType.PERCENT) {
      this.neutrophils = MathUtils.round(
        this.decimalToPercentage(this.neutrophils),
        3,
      );
      this.lymphocytes = MathUtils.round(
        this.decimalToPercentage(this.lymphocytes),
        3,
      );
      this.monocytes = MathUtils.round(
        this.decimalToPercentage(this.monocytes),
        3,
      );
      this.eosinophils = MathUtils.round(
        this.decimalToPercentage(this.eosinophils),
        3,
      );
      this.basophils = MathUtils.round(
        this.decimalToPercentage(this.basophils),
        3,
      );
    } else if (
      wbcDifferentialUnits ===
      WhiteBloodCellDifferentialUnitType.ABSOLUTE_KCELLS
    ) {
      this.neutrophils = this.percentageToAbsolute(this.neutrophils, 3);
      this.lymphocytes = this.percentageToAbsolute(this.lymphocytes, 3);
      this.monocytes = this.percentageToAbsolute(this.monocytes, 3);
      this.eosinophils = this.percentageToAbsolute(this.eosinophils, 3);
      this.basophils = this.percentageToAbsolute(this.basophils, 3);
    } else if (
      wbcDifferentialUnits === WhiteBloodCellDifferentialUnitType.ABSOLUTE_CELLS
    ) {
      this.neutrophils = this.percentageToAbsolute(this.neutrophils, 3) * 1000;
      this.lymphocytes = this.percentageToAbsolute(this.lymphocytes, 3) * 1000;
      this.monocytes = this.percentageToAbsolute(this.monocytes, 3) * 1000;
      this.eosinophils = this.percentageToAbsolute(this.eosinophils, 3) * 1000;
      this.basophils = this.percentageToAbsolute(this.basophils, 3) * 1000;
    }
  }

  convertToDecimal?(value: number): number {
    return value != null ? MathUtils.round(value / 100, 4) : value;
  }

  convertToDecimalFromAbsolute?(
    value: number,
    wbcDifferentialUnits: WhiteBloodCellDifferentialUnitType,
  ): number {
    if (!this.isWhiteBloodCellsDefined()) {
      return value;
    }

    let multiplier: number = 1;
    if (
      wbcDifferentialUnits === WhiteBloodCellDifferentialUnitType.ABSOLUTE_CELLS
    ) {
      multiplier = 1000;
    }

    value =
      value != null
        ? MathUtils.round(value / (this.whiteBloodCells * multiplier), 4)
        : undefined;

    return value;
  }

  percentageToAbsolute?(value: number, precision: number = 3): number {
    if (!this.isWhiteBloodCellsDefined()) {
      return undefined;
    }

    value = value != null ? value * this.whiteBloodCells : undefined;

    return value ? MathUtils.round(value, precision) : value;
  }

  decimalToPercentage?(value: number): number {
    return value != null ? value * 100 : undefined;
  }

  isWhiteBloodCellsDefined?(): boolean {
    const validator = new Validator();
    return validator.isDefined(this.whiteBloodCells);
  }

  hasWBCDifferencial?(): boolean {
    const validator = new Validator();
    return (
      validator.isNumber(this.neutrophils) ||
      validator.isNumber(this.lymphocytes) ||
      validator.isNumber(this.monocytes) ||
      validator.isNumber(this.eosinophils) ||
      validator.isNumber(this.basophils)
    );
  }

  key?(): any {
    return {
      practitionerId: this.practitionerId,
      createDate: this.createDate,
    };
  }

  plateletMCHRatio?(): number {
    if (this.plateletCount != null && this.meanCorpuscularHGB > 0) {
      return this.plateletCount / this.meanCorpuscularHGB;
    }
    return undefined;
  }

  clientAgeAtCollectionDate?(): number {
    return this.client.ageAsOf(this.collectionDate);
  }

  isBilirubinValid?(): boolean {
    if (this.bilirubin == null) return true;
    if (this.bilirubinDirect != null && this.bilirubinDirect > this.bilirubin) {
      return false;
    }
    if (
      this.bilirubinIndirect != null &&
      this.bilirubinIndirect > this.bilirubin
    ) {
      return false;
    }
    if (this.bilirubinDirect != null && this.bilirubinIndirect != null) {
      const totalBilirubin = this.bilirubinDirect + this.bilirubinIndirect;
      return MathUtils.round(totalBilirubin, 2) <= this.bilirubin;
    }
    return true;
  }
}
